<script setup lang="ts">
/**
 * 3D звёздное поле для drawer-меню.
 * Перспективная камера + туман = реальная глубина.
 * Реакция на мышь — параллакс слоёв.
 */

import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  BufferGeometry,
  PointsMaterial,
  Points,
  Float32BufferAttribute,
  CanvasTexture,
  AdditiveBlending,
  Group,
} from 'three';

const canvasRef = ref<HTMLCanvasElement | null>(null);

const { dprCap } = use3DQuality();

const props = defineProps<{
  active: boolean;
}>();

// --- Three.js refs ---
let renderer: WebGLRenderer | null = null;
let scene: Scene | null = null;
let camera: PerspectiveCamera | null = null;
let starsGroup: Group | null = null;
let rafId: number | null = null;
let startTime = 0;

// Позиция мыши (нормализованная, -1..1)
const mouseTarget = { x: 0, y: 0 };
const mouseCurrent = { x: 0, y: 0 };
let isFirstFrame = true;

// --- Текстура точки (мягкое свечение) ---
const createStarTexture = (): CanvasTexture => {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext('2d')!;

  // Внешнее свечение
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255, 255, 255, 1)');
  g.addColorStop(0.15, 'rgba(255, 255, 255, 0.8)');
  g.addColorStop(0.4, 'rgba(255, 255, 255, 0.15)');
  g.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);

  return new CanvasTexture(c);
};

// Сцена инициализируется при первом открытии drawer (когда canvas имеет размеры)
let isSceneInitialized = false;

const initScene = () => {
  const canvas = canvasRef.value;
  if (!canvas || isSceneInitialized) return;

  const rect = canvas.getBoundingClientRect();
  // Проверяем, что canvas имеет размеры (drawer открыт)
  if (rect.width < 10 || rect.height < 10) return;

  const w = rect.width;
  const h = rect.height;
  const dpr = Math.min(window.devicePixelRatio, dprCap.value);

  // Scene (без тумана — фон прозрачный, FBM noise виден)
  scene = new Scene();

  // Camera — перспективная для реальной глубины
  camera = new PerspectiveCamera(45, w / h, 0.1, 100);
  camera.position.set(0, 0, 15);

  // Renderer (прозрачный фон — FBM noise виден сквозь звёзды)
  renderer = new WebGLRenderer({
    canvas,
    antialias: false,
    alpha: true, // прозрачный фон
  });
  renderer.setSize(w, h);
  renderer.setPixelRatio(dpr);
  renderer.setClearColor(0x000000, 0); // полностью прозрачный

  // Звёзды
  starsGroup = new Group();
  scene.add(starsGroup);

  const starTexture = createStarTexture();
  const count = 2500;
  const positions: number[] = [];

  for (let i = 0; i < count; i++) {
    // Распределение: широкий X/Y, глубокий Z
    const x = (Math.random() - 0.5) * 40;
    const y = (Math.random() - 0.5) * 50;
    const z = -Math.random() * 30; // от 0 до -30 (вглубь)
    positions.push(x, y, z);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute(
    'position',
    new Float32BufferAttribute(positions, 3),
  );

  const material = new PointsMaterial({
    size: 0.12,
    map: starTexture,
    blending: AdditiveBlending,
    depthTest: false,
    transparent: true,
    opacity: 0.7,
    color: 0xffffff,
  });

  const points = new Points(geometry, material);
  starsGroup.add(points);

  startTime = performance.now();
  isSceneInitialized = true;
};

// --- Resize ---
const resizeCanvas = () => {
  const canvas = canvasRef.value;
  if (!canvas || !renderer || !camera) return;

  const rect = canvas.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio, dprCap.value);

  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  renderer.setSize(rect.width, rect.height);
  renderer.setPixelRatio(dpr);

  camera.aspect = rect.width / rect.height;
  camera.updateProjectionMatrix();
};

// --- Render loop ---
const render = () => {
  if (!renderer || !scene || !camera || !starsGroup) return;

  const elapsed = (performance.now() - startTime) / 1000;

  // Параллакс камеры от мыши (плавная интерполяция)
  if (isFirstFrame) {
    mouseCurrent.x = mouseTarget.x;
    mouseCurrent.y = mouseTarget.y;
    isFirstFrame = false;
    requestAnimationFrame(() => {
      canvasRef.value?.classList.add('drawer-stars--visible');
    });
  } else {
    mouseCurrent.x += (mouseTarget.x - mouseCurrent.x) * 0.08;
    mouseCurrent.y += (mouseTarget.y - mouseCurrent.y) * 0.08;
  }

  // Смещение камеры — параллакс эффект
  camera.position.x = mouseCurrent.x * 2;
  camera.position.y = mouseCurrent.y * 1.5;
  camera.lookAt(0, 0, -5);

  // Медленное вращение группы — ощущение парения
  starsGroup.rotation.y = Math.sin(elapsed * 0.05) * 0.08;
  starsGroup.rotation.x = Math.cos(elapsed * 0.03) * 0.04;

  renderer.render(scene, camera);
  rafId = requestAnimationFrame(render);
};

const startRender = () => {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(render);
};

const stopRender = () => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
};

// --- Mouse handler ---
const onMouseMove = (e: MouseEvent) => {
  const drawerEl = canvasRef.value?.parentElement;
  if (!drawerEl) return;

  const rect = drawerEl.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;

  // Нормализуем координаты относительно drawer (-1..1)
  mouseTarget.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  mouseTarget.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
};

// --- Реакция на prop active ---
watch(
  () => props.active,
  (isActive) => {
    if (isActive) {
      // Инициализируем сцену при первом открытии (когда canvas имеет размеры)
      initScene();
      isFirstFrame = true;
      resizeCanvas();
      startRender();
    } else {
      stopRender();
      canvasRef.value?.classList.remove('drawer-stars--visible');
    }
  },
);

// --- Lifecycle ---
onMounted(() => {
  // Сцена НЕ инициализируется здесь — drawer ещё закрыт, canvas не имеет размеров
  // initScene() будет вызван при первом active=true (watch выше)
  window.addEventListener('resize', resizeCanvas);
  document.addEventListener('mousemove', onMouseMove);
});

onBeforeUnmount(() => {
  stopRender();
  window.removeEventListener('resize', resizeCanvas);
  document.removeEventListener('mousemove', onMouseMove);

  // Cleanup Three.js
  if (starsGroup) {
    starsGroup.traverse((obj) => {
      if (obj instanceof Points) {
        obj.geometry.dispose();
        if (obj.material instanceof PointsMaterial) {
          obj.material.map?.dispose();
          obj.material.dispose();
        }
      }
    });
    starsGroup = null;
  }

  if (renderer) {
    renderer.dispose();
    renderer = null;
  }

  scene = null;
  camera = null;
});
</script>

<template lang="pug">
canvas.drawer-stars(
  ref="canvasRef"
  aria-hidden="true"
)
</template>

<style lang="sass" scoped>
.drawer-stars
  position: absolute
  top: 0
  left: 0
  width: 100%
  height: 100%
  display: block
  pointer-events: none
  opacity: 0
  transition: opacity 1.2s ease-in

.drawer-stars--visible
  opacity: 1
</style>
